import { Controller, useFormContext, useWatch } from "react-hook-form";
import { JOB_STEPS } from "../CreateJobPage";
import { Slider } from "@components/ui/slider";
import CustomDropdown from "./CustomDropdown";
import FieldError from "./FieldError";
import { ArrowRight } from "lucide-react";
import { Checkbox } from "@components/ui/checkbox";
import { JobFormValues } from "../schema/jobForm.schema";
import {
  BELOW_THRESHOLD_OPTIONS,
  QUEUE_HEALTH_MINIMUM_OPTIONS,
} from "../schema/jobForm.options";

const VISIBILITY_TYPES = [
  {
    label: "Show and rank every match",
    desc: "Keep every matched profile in the main list, ordered from highest to lowest alignment.",
    isBest: false,
    value: "all_matches",
  },
  {
    label: "Use a visibility threshold",
    desc: "Show stronger matches in the main queue and retain the remainder under Filtered candidates.",
    isBest: true,
    value: "threshold_only",
  },
] as const;

const Step6 = ({ activeStep }) => {
  const { control, setValue } = useFormContext<JobFormValues>();
  const [selectedVisibilityType, threshold, queueHealthMinimum] = useWatch({
    control,
    name: ["matchVisibilityMode", "reviewThreshold", "queueHealthMinimum"],
  });

  return (
    <>
      {/* Header */}
      <div className="flex border-b pb-3">
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-xss font-bold text-light-black uppercase">
            Step {String(activeStep).padStart(2, "0")} /{" "}
            {JOB_STEPS[activeStep - 1]?.label}{" "}
          </span>
          <span className="font-semibold text-xl">
            {JOB_STEPS[activeStep - 1]?.title}
          </span>
          <span className="text-xss font-medium text-light-black">
            {JOB_STEPS[activeStep - 1]?.subtitle}
          </span>
        </div>
        <span className="self-end text-xss font-bold text-light-black uppercase leading-3">
          {JOB_STEPS[activeStep - 1]?.tagline}
        </span>
      </div>
      {/* Candidate match visibility */}
      <div className="rounded-md border overflow-hidden mt-5">
        <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
              01
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xs">
                Candidate match visibility
              </span>
              <span className="text-xss font-medium text-light-black">
                Keep the main review queue focused without losing access to
                lower-scoring profiles.
              </span>
            </div>
          </div>

          <div className="rounded-full px-2 py-1 font-bold text-xsss text-slate-600 uppercase bg-slate-200 tracking-wider">
            Explainable matching
          </div>
        </div>

        <div className="flex flex-col p-4">
          {/* Visibility selector */}
          <div className="flex gap-3">
            {VISIBILITY_TYPES?.map((visibilityType) => {
              const isSelected =
                visibilityType?.value === selectedVisibilityType;
              return (
                <button
                  key={visibilityType.value}
                  type="button"
                  onClick={() =>
                    setValue("matchVisibilityMode", visibilityType.value, {
                      shouldDirty: true,
                    })
                  }
                  className={`flex rounded-md p-4 gap-3 flex-1 ${isSelected ? "border-l-[3.5px] border-slate-600 border bg-slate-50" : "border"}`}
                >
                  <div
                    className={`flex items-center justify-center rounded-full border w-4 h-4 shrink-0 ${isSelected ? "bg-slate-600" : "border-light-grey"}`}
                  >
                    <div className="h-1.5 w-1.5 rounded-full bg-white" />
                  </div>
                  <div className="flex flex-col gap-1 flex-1 text-left">
                    <span className="text-black text-sm font-semibold">
                      {visibilityType?.label}
                    </span>
                    <span className="text-xss text-light-black font-medium">
                      {visibilityType?.desc}
                    </span>
                  </div>
                  {visibilityType?.isBest ? (
                    <div className="bg-green-100 text-green-800 rounded-full py-0.5 px-3 uppercase font-bold text-xsss w-max h-max">
                      best for scale
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>

          {/* Row 1 */}
          <div className="grid grid-cols-[1.1fr_0.9fr] gap-3 bg-slate-50 p-3 mt-3 rounded-md border">
            {/* Col 1 */}
            <div className="flex flex-col bg-white rounded-md border p-3 h-max">
              <div className="flex items-center justify-between">
                <span className="font-bold text-xss text-slate-600">
                  Main queue starts at
                </span>
                <span className="font-semibold text-md text-slate-600">
                  {threshold}%
                </span>
              </div>
              <span className="font-medium text-xsss text-light-black">
                Profiles at or above this score appear in the main applicant
                list, ordered from highest match to lowest.
              </span>
              <div className="mt-2">
                <Controller
                  control={control}
                  name="reviewThreshold"
                  render={({ field }) => (
                    <Slider
                      step={5}
                      min={50}
                      max={90}
                      value={[field.value]}
                      onValueChange={([value]) => field.onChange(value)}
                    />
                  )}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xsss font-medium text-light-black">
                    50
                  </span>
                  <span className="text-xsss font-medium text-light-black">
                    60
                  </span>
                  <span className="text-xsss font-medium text-light-black">
                    70
                  </span>
                  <span className="text-xsss font-medium text-light-black">
                    80
                  </span>
                  <span className="text-xsss font-medium text-light-black">
                    90
                  </span>
                </div>
              </div>
            </div>

            {/* Col 2 */}
            <div className="flex flex-col border rounded-md overflow-hidden">
              <div className="flex flex-col p-2 gap-0.5 bg-slate-100">
                <span className="font-bold text-xsss text-slate-600 uppercase tracking-wide">
                  Candidate handling
                </span>
                <span className="font-medium text-xsss text-light-black">
                  One minimum keeps the rule clear whether the match pool
                  contains a few profiles or several thousand.
                </span>
              </div>
              <div className="border-t">
                <div className="flex items-center justify-between bg-white p-3">
                  <div className="flex items-center gap-3">
                    <div className="rounded-md bg-orange-400 h-6 w-[3px]"></div>
                    <div className="flex flex-col">
                      <span className="font-semibold text-xsss">
                        Main applicant list
                      </span>
                      <span className="font-medium text-xsss text-light-black">
                        Visible and sorted highest match first
                      </span>
                    </div>
                  </div>
                  <span className="font-bold text-xss text-content-muted">
                    {threshold}–100%
                  </span>
                </div>

                <div className="flex items-center justify-between bg-white p-3 border-t">
                  <div className="flex items-center gap-3">
                    <div className="rounded-md bg-gray-400 h-6 w-[3px]"></div>
                    <div className="flex flex-col">
                      <span className="font-semibold text-xsss">
                        Filtered candidates
                      </span>
                      <span className="font-medium text-xsss text-light-black">
                        Outside the main list, retained for review
                      </span>
                    </div>
                  </div>
                  <span className="font-bold text-xss text-content-muted">
                    Below {threshold}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2 */}
          <div className="flex gap-3 mt-3">
            <div className="flex flex-col gap-1.5 flex-1 border rounded-md p-3">
              <label className="text-xss font-bold text-slate-600">
                Below-threshold handling
              </label>
              <Controller
                control={control}
                name="belowThresholdHandling"
                render={({ field }) => (
                  <CustomDropdown
                    options={BELOW_THRESHOLD_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <span className="text-xsss text-light-black font-medium">
                No profile is rejected or deleted by this setting.
              </span>
            </div>
            <div className="flex flex-col justify-center gap-0.5 border bg-white rounded-md flex-1 p-3">
              <span className="font-bold text-xsss tracking-wider text-light-black uppercase">
                Default review order
              </span>
              <span className="font-semibold text-xss text-black">
                Highest match first
              </span>
              <span className="font-medium text-xsss text-light-black">
                Agents can still change the sort order from the Applicants page.
              </span>
            </div>
          </div>

          {/* Row 3 */}
          <div className="flex flex-col mt-3 rounded-md border">
            <div className="flex items-center justify-between border-b p-3 w-full bg-slate-100">
              <div className="flex flex-col">
                <span className="text-light-black uppercase text-xsss font-bold tracking-wider">
                  Queue health notification
                </span>
                <span className="font-semibold text-xss">
                  Know when the minimum leaves too few profiles to review.
                </span>
              </div>
              <div className="rounded-full px-1 py-0.5 bg-green-100 text-green-800 uppercase text-xsss font-bold">
                Recommended
              </div>
            </div>

            <div className="flex flex-col bg-slate-50 p-3">
              <div className="flex gap-3">
                <div className="flex gap-3 flex-1 border bg-white rounded-md p-3">
                  <Controller
                    control={control}
                    name="queueHealthNotificationEnabled"
                    render={({ field }) => (
                      <Checkbox
                        id="notify"
                        checked={field.value}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                    )}
                  />
                  <div className="flex flex-col cursor-pointer gap-0.5">
                    <label
                      htmlFor="notify"
                      className="font-semibold text-xss text-black"
                    >
                      Notify me when the main applicant list is running low
                    </label>
                    <label
                      htmlFor="notify"
                      className="font-medium text-xsss text-light-black"
                    >
                      COMTRACKED monitors the number of profiles meeting this
                      job's minimum.
                    </label>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-0.5 border bg-white rounded-md flex-1 p-3">
                  <span className="font-bold text-xsss text-light-black">
                    Send an alert when fewer than
                  </span>
                  <div className="flex items-center">
                    <Controller
                      control={control}
                      name="queueHealthMinimum"
                      render={({ field, fieldState }) => (
                        <div className="flex flex-col">
                          <CustomDropdown
                            options={QUEUE_HEALTH_MINIMUM_OPTIONS}
                            value={field.value}
                            onChange={field.onChange}
                            hasError={!!fieldState.error}
                          />
                          <FieldError message={fieldState.error?.message} />
                        </div>
                      )}
                    />
                    <div className="flex flex-col border-l ml-2 pl-2 w-32">
                      <span className="text-xsss font-medium text-light-black">
                        profile meet
                      </span>
                      <span className="font-semibold text-xss">
                        {threshold}%+
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-5 border-l-[3px] border-green-500 bg-slate-600 px-4 py-3 mt-4">
                <div className="flex flex-col gap-2.5">
                  <span className="uppercase font-bold text-xsss text-gray-200">
                    Example notification
                  </span>
                  <span className="text-xsss font-medium text-white">
                    <span className="font-bold">
                      Only {Math.max(queueHealthMinimum - 1, 0)} candidates meet
                      your {threshold}% minimum.
                    </span>{" "}
                    Review Filtered candidates or lower the minimum to widen the
                    queue.
                  </span>
                </div>

                <button
                  type="button"
                  className="flex items-center p-2 gap-3 font-semibold text-xsss text-white border border-light-grey rounded-md"
                >
                  Review threshold
                  <ArrowRight color="white" size={12} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between border-t p-3.5">
              <div className="flex items-center gap-10">
                <span className="font-bold text-xsss text-light-black uppercase tracking-wide">
                  Delivery
                </span>
                <span className="font-semibold text-xss text-black">
                  In-app notification
                </span>
              </div>
              <span className="font-medium text-xsss text-light-black">
                The alert links back to these settings. COMTRACKED never changes
                the minimum automatically.
              </span>
            </div>
          </div>

          {/* Visibility not rejection */}
          <div className="flex items-center gap-5 border-l-[3px] border-slate-600 bg-slate-100 px-4 py-3 mt-4">
            <span className="uppercase font-bold text-xsss text-slate-600">
              Visibility, not rejection
            </span>
            <span className="text-xsss font-medium text-light-black">
              Match percentages organise the agency workspace only. Filtered
              profiles remain available with their explainable evidence and can
              be restored to the main queue at any time.
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Step6;
